import { forwardRef } from 'react'
import { cn } from '../../utils'

/** Label */
export function Label({ children, required, htmlFor, className }) {
  return (
    <label htmlFor={htmlFor} className={cn('label', className)}>
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  )
}

/** Inline error message */
export function FieldError({ message }) {
  if (!message) return null
  return <p className="error-text">{message}</p>
}

/** Helper text */
export function FieldHelper({ children }) {
  if (!children) return null
  return <p className="helper-text">{children}</p>
}

/** Text / number input */
export const Input = forwardRef(function Input(
  { error, className, ...props },
  ref
) {
  return (
    <input
      ref={ref}
      className={cn('form-input', error && 'form-input-error', className)}
      {...props}
    />
  )
})

/** Select dropdown */
export const Select = forwardRef(function Select(
  { error, children, placeholder, className, ...props },
  ref
) {
  return (
    <select
      ref={ref}
      className={cn('form-select', error && 'border-red-400 bg-red-50', className)}
      {...props}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {children}
    </select>
  )
})

/**
 * FormField — wraps label + input + error in a consistent layout.
 * Usage:
 *   <FormField label="Policy Number" required error={errors.policy?.message}>
 *     <Input {...register('policy')} />
 *   </FormField>
 */
export function FormField({ label, required, error, helper, children, className }) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && <Label required={required}>{label}</Label>}
      {children}
      <FieldError message={error} />
      <FieldHelper>{helper}</FieldHelper>
    </div>
  )
}
